import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FlyOutMenu from '../../../../core/components/FlyOutMenu';
import Button from '../../../../core/components/Button';
import Dialog from '../../../../core/components/Dialog';
import utilSvc from '../../../../core/utilSvc';
import datamonitorRepo, { MonitorDefaultItem, MonitorDefaultSaveModel } from '../../data/datamonitorRepo';

const INTERVAL_TYPES = [
    { label: 'DAYS', value: 'DAY' },
    { label: 'HOURS', value: 'HOUR' },
    { label: 'MINUTES', value: 'MINUTE' },
    { label: 'MONTHS', value: 'MONTH' },
];

type MonitorDefaultsEditFlyOutProps = {
    item: MonitorDefaultItem | null;
    onClose: () => void;
    onSaved: () => void;
    ignoreClickRef?: React.RefObject<HTMLElement>;
};

type FormState = {
    active: string; // 'YES' | 'NO'
    alert: string; // 'ON' | 'OFF'
    intervalVal: string;
    intervalType: string;
    lookbackVal: string;
    lookbackType: string;
};

// legacy: interval/lookback must be whole numbers between 1 and 1,000,000
function invalidInteger(valStr: string): boolean {
    const n = Number(valStr);
    return !Number.isInteger(n) || n < 1 || n > 1000000;
}

const MonitorDefaultsEditFlyOut = ({
    item,
    onClose,
    onSaved,
    ignoreClickRef,
}: MonitorDefaultsEditFlyOutProps): JSX.Element => {
    const [form, setForm] = useState<FormState>({
        active: 'NO',
        alert: 'OFF',
        intervalVal: '',
        intervalType: 'DAY',
        lookbackVal: '',
        lookbackType: 'DAY',
    });
    const [intervalErr, setIntervalErr] = useState('');
    const [lookbackErr, setLookbackErr] = useState('');
    const [dialogMsg, setDialogMsg] = useState('');
    const [saving, setSaving] = useState(false);

    const isDatafeed = item?.ConfigType === 'DATAFEED';
    const showLookback = isDatafeed && !!item?.showLookback;

    // reset the form each time a new row is opened
    useEffect(() => {
        if (!item) return;
        setForm({
            active: item.Active,
            alert: item.Alert,
            intervalVal: String(item.IntervalVal ?? ''),
            intervalType: item.IntervalType || 'DAY',
            lookbackVal: String(item.LookbackVal ?? ''),
            lookbackType: item.LookbackType || 'DAY',
        });
        setIntervalErr('');
        setLookbackErr('');
        setDialogMsg('');
    }, [item]);

    const update = useCallback(<K extends keyof FormState>(key: K, val: FormState[K]): void => {
        setForm(prev => ({ ...prev, [key]: val }));
    }, []);

    const handleSave = useCallback(async (): Promise<void> => {
        if (!item || saving) return;

        let intMsg = '';
        let lookMsg = '';

        if (isDatafeed) {
            if (invalidInteger(form.intervalVal)) {
                intMsg = 'enter a whole number 1 - 1,000,000';
            } else if (!form.intervalType) {
                intMsg = 'required';
            }
            if (showLookback) {
                if (invalidInteger(form.lookbackVal)) {
                    lookMsg = 'enter a whole number 1 - 1,000,000';
                } else if (!form.lookbackType) {
                    lookMsg = 'required';
                }
            }
        }

        setIntervalErr(intMsg);
        setLookbackErr(lookMsg);
        if (intMsg || lookMsg) {
            setDialogMsg('Check data properties');
            return;
        }

        // alerts cannot be on when the monitor is off
        if (form.active === 'NO' && form.alert === 'ON') {
            setDialogMsg('Alerts must be off if Monitor is off.');
            return;
        }

        const model: MonitorDefaultSaveModel = {
            Id: item.Id,
            ConfigType: item.ConfigType,
            ConfigName: item.ConfigName,
            Active: form.active === 'YES',
            Alert: form.alert === 'ON',
            IntervalVal: parseInt(form.intervalVal, 10) || 0,
            IntervalType: form.intervalType,
            LookbackVal: parseInt(form.lookbackVal, 10) || 0,
            LookbackType: form.lookbackType,
        };

        setSaving(true);
        try {
            await datamonitorRepo.saveDefaultsConfig(model);
            onSaved();
        } catch (err: any) {
            // apiSvc already surfaces its own error dialog for handled failures
            if (!utilSvc.http.isIgnore(err)) {
                setDialogMsg('Unable to save the monitor rule. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    }, [item, saving, isDatafeed, showLookback, form, onSaved]);

    const dialogActions = useMemo(() => [{ label: 'OK', onClick: (): void => setDialogMsg('') }], []);

    return (
        <>
            <FlyOutMenu
                title={item ? `${item.ConfigName} Defaults` : ''}
                isOpen={!!item}
                onClose={onClose}
                width={480}
                overflowY="auto"
                ignoreClickRef={ignoreClickRef}
            >
                {item ? (
                    <form
                        className="monitor-defaults-edit"
                        onSubmit={(e): void => {
                            e.preventDefault();
                            handleSave();
                        }}
                    >
                        <div className="monitor-defaults-edit-primary-row">
                            <div className="monitor-defaults-edit-field">
                                <span className="monitor-defaults-edit-label">Active</span>
                                {isDatafeed ? (
                                    <div className="monitor-defaults-edit-radios">
                                        <label>
                                            <input
                                                type="radio"
                                                name="defaultActive"
                                                checked={form.active === 'YES'}
                                                onChange={(): void => update('active', 'YES')}
                                            />
                                            &nbsp;Yes
                                        </label>
                                        <label>
                                            <input
                                                type="radio"
                                                name="defaultActive"
                                                checked={form.active === 'NO'}
                                                onChange={(): void => update('active', 'NO')}
                                            />
                                            &nbsp;No
                                        </label>
                                    </div>
                                ) : (
                                    <span className="monitor-defaults-edit-readonly">{form.active}</span>
                                )}
                            </div>

                            <div className="monitor-defaults-edit-field">
                                <span className="monitor-defaults-edit-label">Alerts</span>
                                <div className="monitor-defaults-edit-radios">
                                    <label>
                                        <input
                                            type="radio"
                                            name="defaultAlert"
                                            checked={form.alert === 'ON'}
                                            onChange={(): void => update('alert', 'ON')}
                                        />
                                        &nbsp;On
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="defaultAlert"
                                            checked={form.alert === 'OFF'}
                                            onChange={(): void => update('alert', 'OFF')}
                                        />
                                        &nbsp;Off
                                    </label>
                                </div>
                            </div>

                            {isDatafeed ? (
                                <div className="monitor-defaults-edit-field">
                                    <span className="monitor-defaults-edit-label">Fails After</span>
                                    <div className="monitor-defaults-edit-interval">
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={10}
                                            placeholder="number"
                                            value={form.intervalVal}
                                            onChange={(e): void => update('intervalVal', e.target.value)}
                                        />
                                        <select
                                            value={form.intervalType}
                                            onChange={(e): void => update('intervalType', e.target.value)}
                                        >
                                            {INTERVAL_TYPES.map(t => (
                                                <option
                                                    key={t.value}
                                                    value={t.value}
                                                >
                                                    {t.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {intervalErr ? (
                                        <span className="monitor-defaults-edit-err">{intervalErr}</span>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>

                        {showLookback ? (
                            <div className="monitor-defaults-edit-field">
                                <span className="monitor-defaults-edit-label">Lookback Range</span>
                                <div className="monitor-defaults-edit-interval">
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={10}
                                        placeholder="number"
                                        value={form.lookbackVal}
                                        onChange={(e): void => update('lookbackVal', e.target.value)}
                                    />
                                    <select
                                        value={form.lookbackType}
                                        onChange={(e): void => update('lookbackType', e.target.value)}
                                    >
                                        {INTERVAL_TYPES.map(t => (
                                            <option
                                                key={t.value}
                                                value={t.value}
                                            >
                                                {t.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {lookbackErr ? <span className="monitor-defaults-edit-err">{lookbackErr}</span> : null}
                            </div>
                        ) : null}

                        <div className="monitor-defaults-edit-actions">
                            <Button
                                id="monitorDefaultsSave"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                SAVE
                            </Button>
                            <Button
                                id="monitorDefaultsCancel"
                                cssName="btn-cancel"
                                onClick={onClose}
                                disabled={saving}
                            >
                                CANCEL
                            </Button>
                        </div>
                    </form>
                ) : null}
            </FlyOutMenu>
            <Dialog
                open={!!dialogMsg}
                setOpen={(): void => setDialogMsg('')}
                title="Cannot Save"
                message={dialogMsg}
                actions={dialogActions}
            />
        </>
    );
};

export default MonitorDefaultsEditFlyOut;
